import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslatePipe } from '@ngx-translate/core';
import { BucketService } from '../../../services/bucket.service';
import { EmbeddingStatusService } from '../../../services/embedding-status.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { FileUploadDialogComponent } from '../../dialogs/file-upload-dialog/file-upload-dialog.component';
import { CloudSyncDialogComponent } from '../../dialogs/cloud-sync-dialog/cloud-sync-dialog.component';
import { FileRowActionsComponent } from '../../features/file-row-actions/file-row-actions.component';
import { FileIndexingStatusComponent } from '../../features/file-indexing-status/file-indexing-status.component';
import { AlTableApi } from '../../features/table/table.component';
import { formatFileSize, formatMegabytes } from '../../../helpers/formatNumbers';

@Component({
  selector: 'app-files-table',
  templateUrl: './files-table.component.html',
  styleUrl: './files-table.component.scss',
})
export class FilesTableComponent implements OnInit, OnDestroy {
  @Input() title = '';
  @Input() bucketId = '';
  @Input() loading = false;
  @Input() searchText = '';

  get filteredData() {
    const data = this.bucketService._files.value;
    if (!this.searchText) return data;
    const q = this.searchText.toLowerCase();
    return data.filter((f) => f.file_name?.toLowerCase().includes(q));
  }

  @ViewChild('table') table!: AlTableApi;

  private readonly CLOUD_LABELS: Record<string, string> = {
    google_drive: '☁ Google Drive',
    dropbox: '☁ Dropbox',
    sharepoint: '☁ SharePoint',
  };

  columns: any[] = [
    { key: 'file_name', title: 'Name', width: '40%', kind: 'name' },
    {
      // Whether the agents can find the file yet — the "Agent" column below
      // only says whether they are allowed to.
      key: '_indexing',
      title: this.t.transform('pages.knowledgeBase.indexing.column'),
      width: '150px',
      component: FileIndexingStatusComponent,
      componentInputs: ({ row }: any) => ({ row }),
    },
    {
      // Sortiert nach den rohen Bytes, angezeigt in B/KB/MB/GB.
      key: 'file_size', kind: 'number',
      title: 'Größe',
      map: (bytes: number | null) => formatFileSize(bytes),
      width: '90px',
    },
    {
      key: 'uploaded_at', kind: 'date',
      title: 'Hochgeladen am',
      map: (i: string) => new DatePipe('de-DE', 'CET').transform(i, 'short'),
    },
    {
      key: 'version_label',
      title: 'Version',
      // Free text the user types. Deliberately not sortable and not read by
      // anything: "v2.0" / "V 2" / "Fassung 2" carry no order, so nothing may
      // infer "the newest" from them.
      map: (v: string) => v || '',
      width: '90px',
    },
    {
      key: 'agent_enabled',
      title: 'Agent',
      map: (v: any) => (v === false ? 'Deaktiviert' : 'Aktiv'),
      width: '110px',
    },
    {
      key: 'cloud_source',
      title: 'Quelle',
      map: (v: string) => v ? (this.CLOUD_LABELS[v] || v) : '',
      width: '120px',
    },
    {
      key: 'last_synced_at', kind: 'date',
      title: 'Letzte Sync',
      map: (i: string) => i ? new DatePipe('de-DE', 'CET').transform(i, 'short') : '',
      width: '120px',
    },
    {
      key: '_actions',
      title: '',
      width: '80px',
      component: FileRowActionsComponent,
      componentInputs: ({ row }: any) => ({ row }),
    },
  ];

  rowActions: any[] = [
    {
      key: 'toggleAgent',
      title: 'Für Agenten deaktivieren / aktivieren',
      icon: 'slash-outline',
    },
    {
      key: 'setVersion',
      title: 'Version vergeben',
      icon: 'hash-outline',
    },
    {
      key: 'delete',
      title: this.t.transform('buttons.delete'),
      isMultiAction: true,
      icon: 'trash-outline',
    },
  ];

  globalActions = [
    {
      key: 'add',
      title: this.t.transform('buttons.uploadFiles'),
      icon: 'plus-outline',
    },
    {
      key: 'syncCloud',
      title: this.t.transform('buttons.syncFromCloud'),
      icon: 'cloud-upload-outline',
    },
  ];

  /** Auswahlleiste: Gesamtgröße der markierten Dateien in MB — gleiche
   *  Einheit wie die Speicher-Kachel, damit man sieht, was ein Löschen
   *  freigibt. Feste Funktion, kein Getter (CLAUDE.md Regel 18). */
  readonly selectionSummary = (rows: any[]): string =>
    formatMegabytes(rows.reduce((sum, row) => sum + (Number(row?.file_size) || 0), 0));

  private filesSub?: Subscription;

  constructor(
    protected bucketService: BucketService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslatePipe,
    private embeddingStatus: EmbeddingStatusService
  ) {}

  ngOnInit(): void {
    // Poll the indexing state of the listed files while any is in progress.
    this.filesSub = this.bucketService._files.subscribe((files) =>
      this.embeddingStatus.watch(files)
    );
  }

  ngOnDestroy(): void {
    this.filesSub?.unsubscribe();
    this.embeddingStatus.stop();
  }

  onEvent(e: { event: string; data: any }) {
    const { action, ids, rows, id, row } = e.data;

    switch (e.event) {
      case 'multiAction':
        if (action.key === 'delete') {
          this.deleteIds(ids);
          return;
        }

        break;
      case 'rowAction':
        if (action.key === 'delete') {
          this.deleteIds([id]);
        }
        if (action.key === 'toggleAgent') {
          this.toggleAgentAccess(row);
        }
        if (action.key === 'setVersion') {
          this.setVersion(row);
        }
        break;
      case 'globalAction':
        if (action.key === 'add') {
          this.dialogService.open(FileUploadDialogComponent, {
            context: {
              bucketId: this.bucketId,
            },
          });
          return;
        }
        if (action.key === 'syncCloud') {
          this.dialogService.open(CloudSyncDialogComponent, {
            context: { bucketId: this.bucketId },
            hasScroll: true,
            closeOnBackdropClick: true,
          });
          return;
        }
        break;
      default:
        break;
    }
  }

  /** Datei behalten, aber der Agent durchsucht sie nicht mehr — und zurück.
   *
   *  Wirkt sofort: die Ausschlussliste wird bei jeder Nachricht neu aufgelöst,
   *  es müssen keine Chunks umgeschrieben werden.
   */
  toggleAgentAccess(row: any) {
    const datatypeId = row?.datatype_id || row?.id;
    if (!datatypeId) return;
    const next = row.agent_enabled === false;
    this.bucketService.setFileAgentAccess(datatypeId, { agent_enabled: next }).subscribe({
      next: () => {
        row.agent_enabled = next;
        this.nbToastrService.success(
          next
            ? 'Die Datei wird wieder durchsucht.'
            : 'Die Datei bleibt erhalten, der Agent durchsucht sie nicht mehr.',
          'Wissensbasis'
        );
      },
      error: () =>
        this.nbToastrService.danger('Änderung fehlgeschlagen', 'Wissensbasis'),
    });
  }

  /** Freitext-Etikett setzen oder löschen.
   *
   *  Über die Portal-eigene ConfirmDialogComponent mit Textfeld — nicht über
   *  `window.prompt`: das ignoriert das Design-System und schreibt den Hostnamen
   *  in seine Titelzeile ("Auf dev.portal.alphabees.de wird Folgendes angezeigt").
   */
  setVersion(row: any) {
    const datatypeId = row?.datatype_id || row?.id;
    if (!datatypeId) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.confirmDialog.fileVersion.title',
          message: 'dialogs.confirmDialog.fileVersion.message',
          inputLabel: 'dialogs.confirmDialog.fileVersion.label',
          inputPlaceholder: 'dialogs.confirmDialog.fileVersion.placeholder',
          inputValue: row.version_label || '',
          inputMaxLength: 40,
          confirmLabel: 'buttons.save',
        },
      })
      .onClose.subscribe((result: any) => {
        // Mit aktivem Textfeld schließt der Dialog als Objekt — ein leerer
        // String ist eine gültige Antwort (Etikett entfernen) und wäre als
        // blanker Rückgabewert nicht von "Abbrechen" zu unterscheiden.
        if (!result?.confirmed) return;
        const label = (result.value || '').slice(0, 40);
        this.bucketService
          .setFileAgentAccess(datatypeId, { version_label: label })
          .subscribe({
            next: () => {
              row.version_label = label || null;
              this.nbToastrService.success('Version gespeichert', 'Wissensbasis');
            },
            error: () =>
              this.nbToastrService.danger('Speichern fehlgeschlagen', 'Wissensbasis'),
          });
      });
  }

  deleteIds(ids: string[]) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.confirmDialog.deleteEntries.title',
          message: 'dialogs.confirmDialog.deleteEntries.title',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) {
          this.table.isLoading = true;
          this.bucketService.deleteDatatype(ids).subscribe(() => {
            this.table.deselectAll();
            this.table.isLoading = false;
          });
        }
      });
  }
}
